import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import type { ForgotPasswordInput } from '@order/graphql';
import { useForgotPasswordMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

export const useForgotPassword = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  const [forgotPasswordResponse, forgotPasswordMutation] =
    useForgotPasswordMutation();
  const forgotPasswordResponseData =
    forgotPasswordResponse.data?.forgotPassword;

  const isForgotPasswordLoading = forgotPasswordResponse.fetching;
  const isForgotPasswordComplete =
    forgotPasswordResponse.data?.forgotPassword.__typename ===
    'ForgotPasswordSuccess';

  // ────────────────────────────────────────────────────────────────────

  const forgotPassword = useCallback(
    async (input: ForgotPasswordInput) => {
      const response = await forgotPasswordMutation({ input });
      const data = response?.data?.forgotPassword;

      if (data?.__typename === 'ValidationError' || response.error) {
        const userError = t('forgot-password.error');
        const systemError = getUrqlError(response.error);

        track('forgot-password.failure', { userError, systemError });

        addNoticeBanner({
          text: userError,
          palette: 'caution',
        });

        return;
      }

      if (data?.__typename === 'ForgotPasswordSuccess') {
        track('forgot-password.success');
        addNoticeBanner({
          text: t('forgot-password.success'),
          palette: 'success',
        });
      }
    },
    [forgotPasswordMutation, t, track, addNoticeBanner],
  );

  return {
    forgotPassword,
    forgotPasswordResponseData,
    isForgotPasswordComplete,
    isForgotPasswordLoading,
  };
};
